/**
 * AdminReports — preset report tiles + a "custom" builder shell.
 *
 * Each preset is one-click: pick a date range, click Generate, get a
 * preview + a CSV export button. This is the sketch — the real thing
 * will render charts and back the numbers via aggregate queries.
 */

import { useState } from "react";
import { toast } from "sonner";
import { Download, LineChart, Calendar } from "lucide-react";
import { SectionHeader, AdminButton, KpiLine } from "./_ui";
import { REPORT_PRESETS, ADMIN_METRICS } from "../../data/adminMock";

const RANGES = [
  { key: "7d",  label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
];

// Fake preview payload for whichever preset is selected.
const previewFor = (id) => {
  switch (id) {
    case "totals-raised":
      return [
        ["Total raised", "$128,740"],
        ["Number of gifts", "1,569"],
        ["Median gift", "$50"],
        ["Top gift", "$5,000"],
      ];
    case "avg-gift":
      return [
        ["Mean gift", "$82"],
        ["Median gift", "$50"],
        ["Top decile threshold", "$250"],
        ["% anonymous", "34%"],
      ];
    case "avg-request":
      return [
        ["Overall average", "$1,780"],
        ["Emergency housing", "$1,850"],
        ["Books & MCAT", "$1,120"],
        ["Winter gear", "$620"],
      ];
    case "top-schools":
      return [
        ["Howard University", "8 campaigns · $18,200"],
        ["Stanford", "6 campaigns · $16,400"],
        ["Prairie View A&M", "5 campaigns · $9,320"],
        ["UC Berkeley", "4 campaigns · $8,940"],
      ];
    case "top-categories":
      return [
        ["Books & MCAT", "38%"],
        ["Winter gear", "17%"],
        ["Emergency housing", "14%"],
        ["Move-in essentials", "13%"],
      ];
    case "verification-sla":
      return [
        ["Median days", `${ADMIN_METRICS.daysToVerifyAvg}`],
        ["P95 days", "3.4"],
        ["Fastest month", "Jan '26 — 0.9 d"],
        ["Slowest month", "Dec '25 — 2.6 d"],
      ];
    case "top-donors":
      return [
        ["The Bell Family", "$5,000 · this month"],
        ["Codrington Foundation", "$42,500 · this year"],
        ["Anonymous (aggregate)", "$18,900"],
        ["K. Sparks", "$3,240"],
      ];
    default:
      return [];
  }
};

export const AdminReports = () => {
  const [range, setRange] = useState("30d");
  const [activePreset, setActivePreset] = useState(REPORT_PRESETS[0].id);

  const preset = REPORT_PRESETS.find((p) => p.id === activePreset);
  const preview = previewFor(activePreset);

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Reports"
        title="Answer the questions the board will ask."
        description="Pick a report, choose a window, generate. Every report exports as CSV."
        action={
          <AdminButton
            variant="primary"
            size="sm"
            onClick={() => toast.success("CSV export queued")}
            testid="reports-export"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> Export CSV
          </AdminButton>
        }
      />

      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="h-4 w-4 text-ink/50" strokeWidth={1.75} />
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            data-testid={`range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${
              range === r.key ? "bg-ink text-sand" : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Preset picker */}
        <div className="lg:col-span-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Available reports</p>
          <ul className="mt-3 space-y-2">
            {REPORT_PRESETS.map((p) => {
              const isActive = p.id === activePreset;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    data-testid={`preset-${p.id}`}
                    onClick={() => setActivePreset(p.id)}
                    className={`flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition ${
                      isActive
                        ? "border-ink bg-ink text-sand"
                        : "border-ink/10 bg-sand text-ink hover:border-ink/40"
                    }`}
                  >
                    <LineChart className={`mt-0.5 h-4 w-4 shrink-0 ${isActive ? "text-sand" : "text-ink/60"}`} strokeWidth={1.75} />
                    <div>
                      <p className="text-sm font-medium">{p.label}</p>
                      <p className={`mt-0.5 text-xs leading-snug ${isActive ? "text-sand/70" : "text-ink/60"}`}>
                        {p.desc}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
            Preview — {RANGES.find((r) => r.key === range).label}
          </p>
          <div className="mt-3 rounded-lg border border-ink/10 bg-sand p-6">
            <h3 className="font-serif text-2xl text-ink">{preset.label}</h3>
            <p className="mt-1 text-sm text-ink/60">{preset.desc}</p>
            <div className="mt-6">
              {preview.map(([k, v]) => (
                <KpiLine key={k} label={k} value={v} />
              ))}
            </div>
            <p className="mt-6 text-[11px] italic text-ink/50">
              TODO(backend): real values will come from aggregate queries + a chart renderer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
